import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Plus, Users, Bot, Coins, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClickableAvatar } from '../../components/UserStatsModal';

// RBXFlip style Bux chip (green poker chip with R$)
const BuxChip = ({ size = 64 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size}>
    <circle cx="50" cy="50" r="48" fill="#22c55e" />
    {[...Array(12)].map((_, i) => (
      <rect key={i} x="46" y="2" width="8" height="10" rx="2" fill="white" transform={`rotate(${i * 30} 50 50)`} />
    ))}
    <circle cx="50" cy="50" r="34" fill="#16a34a" />
    <circle cx="50" cy="50" r="28" fill="#15803d" />
    <text x="50" y="58" textAnchor="middle" fontSize="24" fontWeight="bold" fill="white" fontFamily="Arial">R$</text>
  </svg>
);

// RBXFlip style Tix chip (orange poker chip with T)
const TixChip = ({ size = 64 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size}>
    <circle cx="50" cy="50" r="48" fill="#f97316" />
    {[...Array(12)].map((_, i) => (
      <rect key={i} x="46" y="2" width="8" height="10" rx="2" fill="white" transform={`rotate(${i * 30} 50 50)`} />
    ))}
    <circle cx="50" cy="50" r="34" fill="#ea580c" />
    <circle cx="50" cy="50" r="28" fill="white" />
    <text x="50" y="60" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#ea580c" fontFamily="Arial">T</text>
  </svg>
);

const DefaultAvatar = ({ size = 48 }) => (
  <div 
    className="rounded-full bg-slate-700 flex items-center justify-center"
    style={{ width: size, height: size }}
  >
    <Users size={size * 0.5} className="text-slate-500" />
  </div>
);

// House Bot Avatar
const HouseAvatar = ({ size = 64 }) => (
  <div 
    className="rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center border-2 border-slate-600"
    style={{ width: size, height: size }}
  >
    <Bot size={size * 0.5} className="text-blue-400" />
  </div>
);

// Result Display Component for vs House mode - shows for 1 minute
const HouseGameResult = ({ result, user, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const playerWon = result.won;
  const resultSide = result.result; // 'bux' or 'tix'
  const playerChoice = result.choice;
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-gradient-to-r from-slate-800/90 to-slate-900/90 rounded-2xl p-6 border border-slate-700/50"
    >
      <div className="flex items-center justify-between">
        {/* Player */}
        <div className="flex flex-col items-center gap-2">
          <motion.div 
            className={`relative ${playerWon ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : 'opacity-50 grayscale'}`}
            animate={playerWon ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 0.5, repeat: playerWon ? 2 : 0 }}
          >
            <ClickableAvatar
              userId={user?.user_id}
              username={user?.username}
              avatarUrl={user?.roblox_avatar_url}
              size={80}
              borderColor="border-green-500"
            />
            <div className="absolute -bottom-1 -right-1">
              {playerChoice === 'bux' ? <BuxChip size={28} /> : <TixChip size={28} />}
            </div>
            {playerWon && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </motion.div>
          <p className={`font-bold text-sm ${playerWon ? 'text-blue-400' : 'text-slate-400'}`}>
            {user?.username || 'You'}
          </p>
          <p className="text-slate-500 text-xs">R${result.amount?.toLocaleString() || '0'}</p>
        </div>

        {/* Center - Result */}
        <div className="flex flex-col items-center gap-3">
          <motion.div
            initial={{ rotateY: 0 }}
            animate={{ rotateY: [0, 360, 720, 1080] }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            {resultSide === 'bux' ? <BuxChip size={72} /> : <TixChip size={72} />}
          </motion.div>
          <span className="text-white font-bold text-lg">
            {resultSide === 'bux' ? 'R$ (Bux)' : 'Tix'}
          </span>
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-xl font-bold ${playerWon ? 'text-blue-400' : 'text-red-400'}`}
          >
            {playerWon ? `+R$${result.payout?.toFixed(0) || '0'}` : `-R$${result.amount?.toFixed(0) || '0'}`}
          </motion.p>
        </div>

        {/* House */}
        <div className="flex flex-col items-center gap-2">
          <motion.div 
            className={`relative ${!playerWon ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : 'opacity-50 grayscale'}`}
            animate={!playerWon ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 0.5, repeat: !playerWon ? 2 : 0 }}
          >
            <HouseAvatar size={80} />
            <div className="absolute -bottom-1 -left-1">
              {playerChoice === 'bux' ? <TixChip size={28} /> : <BuxChip size={28} />}
            </div>
            {!playerWon && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -left-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </motion.div>
          <p className={`font-bold text-sm ${!playerWon ? 'text-blue-400' : 'text-slate-400'}`}>
            House
          </p>
          <p className="text-slate-500 text-xs">R${result.amount?.toLocaleString() || '0'}</p>
        </div>
      </div>

      {/* Winner announcement */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-6 text-center"
      >
        <p className={`text-2xl font-bold ${playerWon ? 'text-blue-400' : 'text-red-400'}`}>
          {playerWon ? 'You Won!' : 'House Wins!'}
        </p>
      </motion.div>
    </motion.div>
  );
};

// Spinning Coin Animation Component
const SpinningCoin = ({ showResult, resultSide }) => {
  return (
    <div className="relative" style={{ perspective: '1000px' }}>
      <motion.div
        className="w-16 h-16 relative"
        initial={{ rotateX: 0 }}
        animate={showResult ? { 
          rotateX: [0, 360, 720, 1080, 1440, 1800, resultSide === 'bux' ? 1800 : 1980],
          y: [0, -30, -20, -15, -10, 0, 0]
        } : { 
          rotateX: [0, 360, 720, 1080, 1440, 1800, 2160, 2520],
          y: [0, -40, -30, -20, -30, -40, -30, 0]
        }}
        transition={{ 
          duration: showResult ? 2.5 : 1.5, 
          ease: showResult ? "easeOut" : "linear",
          repeat: showResult ? 0 : Infinity 
        }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <BuxChip size={64} />
        </div>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateX(180deg)' }}
        >
          <TixChip size={64} />
        </div>
      </motion.div>
    </div>
  );
};

// P2P Game Card - Matching the reference design
const P2PGameCard = ({ game, currentUser, onJoin, onCancel, joining }) => {
  const [battleState, setBattleState] = useState('waiting');
  const [winner, setWinner] = useState(null);
  const [winnerChoice, setWinnerChoice] = useState(null);
  const isCreator = currentUser?.user_id === game.creator_id;
  const opponentChoice = game.creator_choice === 'bux' ? 'tix' : 'bux';

  const handleJoin = async () => {
    setBattleState('flipping');
    const result = await onJoin(game.game_id, game.amount);
    if (result) {
      setTimeout(() => {
        setWinner(result.winner_id);
        const winningChoice = result.winner_id === game.creator_id ? game.creator_choice : opponentChoice;
        setWinnerChoice(winningChoice);
        setBattleState('result');
      }, 2500);
      // Show result for 1 minute (60000ms)
      setTimeout(() => {
        setBattleState('waiting');
        setWinner(null);
        setWinnerChoice(null);
      }, 60000);
    } else {
      setBattleState('waiting');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 rounded-2xl p-4 border border-slate-700/50 hover:border-slate-600/50 transition-all"
    >
      <div className="flex items-center justify-between">
        {/* Player 1 (Creator) */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <motion.div 
              className={`${battleState === 'result' && winner === game.creator_id ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : ''} 
              ${battleState === 'result' && winner !== game.creator_id ? 'opacity-40 grayscale' : ''}`}
              animate={battleState === 'flipping' ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.5, repeat: battleState === 'flipping' ? Infinity : 0 }}
            >
              <ClickableAvatar
                userId={game.creator_id}
                username={game.creator_username}
                avatarUrl={game.creator_avatar}
                size={64}
                borderColor={game.creator_choice === 'bux' ? 'border-green-500' : 'border-orange-500'}
              />
            </motion.div>
            <div className="absolute -bottom-1 -right-1">
              {game.creator_choice === 'bux' ? <BuxChip size={24} /> : <TixChip size={24} />}
            </div>
            {battleState === 'result' && winner === game.creator_id && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </div>
          <div className="hidden sm:block">
            <p className={`font-medium text-sm truncate max-w-[80px] ${battleState === 'result' && winner === game.creator_id ? 'text-blue-400' : 'text-white'}`}>{game.creator_username}</p>
            <p className="text-slate-500 text-xs">R${game.amount.toLocaleString()}</p>
          </div>
        </div>

        {/* Center - VS and Prize */}
        <div className="flex flex-col items-center gap-1">
          {battleState === 'flipping' ? (
            <SpinningCoin showResult={false} resultSide={null} />
          ) : battleState === 'result' && winnerChoice ? (
            <motion.div
              initial={{ scale: 1.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              {winnerChoice === 'bux' ? <BuxChip size={56} /> : <TixChip size={56} />}
            </motion.div>
          ) : (
            <>
              <span className="text-slate-400 font-bold text-lg">VS</span>
              <span className="text-blue-400 font-bold text-lg">R${(game.amount * 2).toLocaleString()}</span>
            </>
          )}
        </div>

        {/* Player 2 (Opponent or Waiting) */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:block text-right">
            <p className={`font-medium text-sm ${battleState === 'result' && winner && winner !== game.creator_id ? 'text-blue-400' : 'text-slate-400'}`}>
              {battleState !== 'waiting' ? (currentUser?.username || 'Opponent') : 'Waiting...'}
            </p>
            <p className="text-slate-500 text-xs">R${game.amount.toLocaleString()}</p>
          </div>
          <div className="relative">
            <motion.div 
              className={`w-16 h-16 rounded-full overflow-hidden border-3 border-dashed ${
                opponentChoice === 'bux' ? 'border-green-500/50' : 'border-orange-500/50'
              } ${battleState === 'result' && winner && winner !== game.creator_id ? 'ring-4 ring-blue-400 ring-opacity-70 border-solid' : ''} 
              ${battleState === 'result' && winner === game.creator_id ? 'opacity-40 grayscale' : ''} 
              flex items-center justify-center bg-slate-800/50`}
              animate={battleState === 'flipping' ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.5, repeat: battleState === 'flipping' ? Infinity : 0, delay: 0.25 }}
            >
              {battleState !== 'waiting' && currentUser ? (
                currentUser.roblox_avatar_url ? (
                  <img src={currentUser.roblox_avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <DefaultAvatar size={64} />
                )
              ) : (
                <HelpCircle className="text-slate-600" size={32} />
              )}
            </motion.div>
            <div className="absolute -bottom-1 -left-1">
              {opponentChoice === 'bux' ? <BuxChip size={24} /> : <TixChip size={24} />}
            </div>
            {battleState === 'result' && winner && winner !== game.creator_id && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -left-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Action Button */}
      {battleState === 'waiting' && (
        <div className="mt-4 flex justify-center">
          {isCreator ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancel(game.game_id)}
              className="text-red-400 border-red-500/50 hover:bg-red-500/10"
            >
              Cancel Game
            </Button>
          ) : (
            <Button
              onClick={handleJoin}
              disabled={joining === game.game_id}
              className="btn-primary px-6"
            >
              {joining === game.game_id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>Join R${game.amount.toLocaleString()}</>
              )}
            </Button>
          )}
        </div>
      )}
      
      {battleState === 'result' && winner && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 text-center"
        >
          <p className={`font-bold ${winner === currentUser?.user_id ? 'text-blue-400' : 'text-red-400'}`}>
            {winner === currentUser?.user_id ? `You won R$${(game.amount * 2).toLocaleString()}!` : 'You lost!'}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

// P2P Completed Game Result Display - Shows for 1 minute
const P2PCompletedGameCard = ({ game, currentUser }) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const winnerId = game.winner_id;
  const isCreatorWinner = winnerId === game.creator_id;
  const isCurrentUserWinner = winnerId === currentUser?.user_id;
  const isCurrentUserCreator = currentUser?.user_id === game.creator_id;
  const opponentChoice = game.creator_choice === 'bux' ? 'tix' : 'bux';
  const winningChoice = isCreatorWinner ? game.creator_choice : opponentChoice;

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-r from-slate-800/90 to-slate-900/90 rounded-2xl p-4 border border-blue-500/50"
    >

      <div className="flex items-center justify-between">
        {/* Player 1 (Creator) */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <motion.div 
              className={`${isCreatorWinner ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : 'opacity-40 grayscale'}`}
              animate={isCreatorWinner ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.5, repeat: isCreatorWinner ? 2 : 0 }}
            >
              <ClickableAvatar
                userId={game.creator_id}
                username={game.creator_username}
                avatarUrl={game.creator_avatar}
                size={64}
                borderColor={game.creator_choice === 'bux' ? 'border-green-500' : 'border-orange-500'}
              />
            </motion.div>
            <div className="absolute -bottom-1 -right-1">
              {game.creator_choice === 'bux' ? <BuxChip size={24} /> : <TixChip size={24} />}
            </div>
            {isCreatorWinner && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </div>
          <div className="hidden sm:block">
            <p className={`font-medium text-sm truncate max-w-[80px] ${isCreatorWinner ? 'text-blue-400' : 'text-slate-400'}`}>
              {game.creator_username}
            </p>
            <p className="text-slate-500 text-xs">R${game.amount.toLocaleString()}</p>
          </div>
        </div>

        {/* Center - Result */}
        <div className="flex flex-col items-center gap-2">
          <motion.div
            initial={{ scale: 1.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            {winningChoice === 'bux' ? <BuxChip size={56} /> : <TixChip size={56} />}
          </motion.div>
          <span className="text-white font-bold text-sm">
            {winningChoice === 'bux' ? 'Bux' : 'Tix'}
          </span>
          <span className="text-blue-400 font-bold">R${(game.amount * 2).toLocaleString()}</span>
        </div>

        {/* Player 2 (Opponent/Joiner) */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:block text-right">
            <p className={`font-medium text-sm ${!isCreatorWinner ? 'text-blue-400' : 'text-slate-400'}`}>
              {currentUser?.username || 'Opponent'}
            </p>
            <p className="text-slate-500 text-xs">R${game.amount.toLocaleString()}</p>
          </div>
          <div className="relative">
            <motion.div 
              className={`${!isCreatorWinner ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : 'opacity-40 grayscale'}`}
              animate={!isCreatorWinner ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.5, repeat: !isCreatorWinner ? 2 : 0 }}
            >
              <ClickableAvatar
                userId={currentUser?.user_id}
                username={currentUser?.username}
                avatarUrl={currentUser?.roblox_avatar_url}
                size={64}
                borderColor={opponentChoice === 'bux' ? 'border-green-500' : 'border-orange-500'}
              />
            </motion.div>
            <div className="absolute -bottom-1 -left-1">
              {opponentChoice === 'bux' ? <BuxChip size={24} /> : <TixChip size={24} />}
            </div>
            {!isCreatorWinner && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -left-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Result Message */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-4 text-center"
      >
        <p className={`text-lg font-bold ${isCurrentUserWinner ? 'text-blue-400' : 'text-red-400'}`}>
          {isCurrentUserWinner ? `You won R$${(game.amount * 2).toLocaleString()}!` : 'You lost!'}
        </p>
      </motion.div>
    </motion.div>
  );
};

export default function Coinflip() {
  const { user, api, refreshUser, refreshRobloxProfile } = useAuth();
  const [mode, setMode] = useState('p2p');
  const [activeGames, setActiveGames] = useState([]);
  const [completedP2PGames, setCompletedP2PGames] = useState([]); // Store completed games for 1 min display
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(null);
  
  const [showCreate, setShowCreate] = useState(false);
  const [betAmount, setBetAmount] = useState('100');
  const [choice, setChoice] = useState('bux');
  
  const [gameResult, setGameResult] = useState(null);
  const [flipping, setFlipping] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'RBX.Casino | Coinflip';
  }, []);

  const fetchGames = async () => {
    try {
      const res = await api.get('/games/coinflip/p2p/active');
      // Filter out games that are in completedP2PGames (we're showing them separately)
      const completedIds = completedP2PGames.map(g => g.game_id);
      const newActiveGames = (res.data.games || []).filter(g => !completedIds.includes(g.game_id));
      setActiveGames(newActiveGames);
    } catch (error) {
      console.error('Failed to fetch games');
    }
  };

  const fetchRecentCompleted = async () => {
    if (!user) return;
    try {
      const res = await api.get('/games/coinflip/p2p/recent/completed');
      const recentGames = res.data.games || [];
      
      // Add any new completed games that we don't already have
      recentGames.forEach(game => {
        setCompletedP2PGames(prev => {
          const exists = prev.some(g => g.game_id === game.game_id);
          if (!exists) {
            // Schedule removal after 60 seconds from when we found it
            setTimeout(() => {
              setCompletedP2PGames(p => p.filter(g => g.game_id !== game.game_id));
            }, 60000);
            return [...prev, { ...game, completedAt: Date.now() }];
          }
          return prev;
        });
      });
    } catch (error) {
      console.error('Failed to fetch recent completed games');
    }
  };

  useEffect(() => {
    fetchGames();
    fetchRecentCompleted();
    // Refresh Roblox avatar on page load
    if (user?.roblox_user_id) {
      refreshRobloxProfile();
    }
    const interval = setInterval(() => {
      fetchGames();
      fetchRecentCompleted();
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate stats
  const totalValue = activeGames.reduce((sum, g) => sum + (g.amount * 2), 0);
  const totalGames = activeGames.length;

  const handleHousePlay = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to play');
      return;
    }

    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount < 10) {
      toast.error('Minimum bet is R$10');
      return;
    }
    if (amount > user.site_balance) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    setFlipping(true);
    setGameResult(null); // Clear previous result
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000)); // Longer flip animation
      
      const res = await api.post('/games/coinflip/play', { amount, choice });
      setGameResult({
        ...res.data,
        choice: choice, // Store player's choice
        amount: amount, // Store bet amount
        mode: 'house'
      });
      
      if (res.data.won) {
        toast.success(`You won R$${res.data.payout.toFixed(0)}!`);
      } else {
        toast.error('You lost! Better luck next time.');
      }
      
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Game failed');
    } finally {
      setLoading(false);
      setFlipping(false);
    }
  };

  const clearGameResult = () => {
    setGameResult(null);
  };

  const handleCreateP2P = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to play');
      return;
    }

    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount < 10) {
      toast.error('Minimum bet is R$10');
      return;
    }
    if (amount > user.site_balance) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      await api.post('/games/coinflip/p2p/create', { amount, choice });
      toast.success('Game created! Waiting for opponent...');
      setShowCreate(false);
      setBetAmount('100');
      refreshUser();
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGame = async (gameId, amount) => {
    if (!user) {
      toast.error('Please login to play');
      return null;
    }
    if (amount > user.site_balance) {
      toast.error('Insufficient balance');
      return null;
    }

    setJoining(gameId);
    try {
      const res = await api.post('/games/coinflip/p2p/join', { game_id: gameId });
      refreshUser();
      
      // Find the game in activeGames to get full details
      const game = activeGames.find(g => g.game_id === gameId);
      if (game && res.data) {
        // Add to completed games with result data
        const completedGame = {
          ...game,
          result: res.data,
          winner_id: res.data.winner_id,
          completedAt: Date.now()
        };
        setCompletedP2PGames(prev => [...prev, completedGame]);
        
        // Remove from active games immediately
        setActiveGames(prev => prev.filter(g => g.game_id !== gameId));
        
        // Remove from completed games after 60 seconds
        setTimeout(() => {
          setCompletedP2PGames(prev => prev.filter(g => g.game_id !== gameId));
        }, 60000);
      }
      
      if (res.data.won) {
        toast.success(`You won R$${res.data.payout.toFixed(0)}!`);
      } else {
        toast.error('You lost! Better luck next time.');
      }
      
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join game');
      return null;
    } finally {
      setJoining(null);
    }
  };

  const handleCancelGame = async (gameId) => {
    try {
      await api.post(`/games/coinflip/p2p/cancel/${gameId}`);
      toast.success('Game cancelled');
      refreshUser();
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel game');
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-white">Coinflip</h1>
      </div>

      {/* Mode Tabs */}
      <div className="flex gap-2 p-1 bg-slate-800/50 rounded-xl w-fit">
        <button
          onClick={() => setMode('p2p')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            mode === 'p2p'
              ? 'bg-blue-600 text-white'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          <Users size={18} />
          P2P
        </button>
        <button
          onClick={() => setMode('house')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            mode === 'house'
              ? 'bg-blue-600 text-white'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          <Bot size={18} />
          vs House
        </button>
      </div>

      {mode === 'p2p' ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <Coins className="text-blue-200" size={28} />
                <div>
                  <p className="text-3xl font-bold text-white">{totalValue.toLocaleString()}</p>
                  <p className="text-blue-200 text-sm">Total Value</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <Users className="text-orange-200" size={28} />
                <div>
                  <p className="text-3xl font-bold text-white">{totalGames}</p>
                  <p className="text-orange-200 text-sm">Active Games</p>
                </div>
              </div>
            </div>
          </div>

          {/* Create Game Button */}
          <div className="flex justify-center">
            <Button
              onClick={() => setShowCreate(!showCreate)}
              className="btn-primary gap-2 px-6"
            >
              <Plus size={18} />
              Create Game
            </Button>
          </div>

          {/* Create Game Form */}
          <AnimatePresence>
            {showCreate && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <form onSubmit={handleCreateP2P} className="glass rounded-2xl p-6 space-y-4">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Bet Amount (R$)</Label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(e.target.value)}
                      min="10"
                      className="input-default"
                      placeholder="100"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 mb-2 block">Your Side</Label>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setChoice('bux')}
                        className={`flex-1 p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          choice === 'bux'
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <BuxChip size={48} />
                        <span className="text-white font-medium">R$ (Bux)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setChoice('tix')}
                        className={`flex-1 p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          choice === 'tix'
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <TixChip size={48} />
                        <span className="text-white font-medium">Tix</span>
                      </button>
                    </div>
                  </div>
                  
                  <Button type="submit" disabled={loading} className="w-full btn-primary">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Create Game for R$${betAmount || 0}`}
                  </Button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Completed Games Results - Show for 1 minute */}
          {completedP2PGames.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-blue-400">Recent Results</h2>
              <AnimatePresence>
                {completedP2PGames.map((game) => (
                  <P2PCompletedGameCard
                    key={game.game_id}
                    game={game}
                    currentUser={user}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Active Games List */}
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">Active Games</h2>
            
            {activeGames.length === 0 && completedP2PGames.length === 0 ? (
              <div className="text-center py-12 glass rounded-2xl">
                <Users className="mx-auto text-slate-600 mb-4" size={48} />
                <p className="text-slate-400">No active games</p>
                <p className="text-slate-500 text-sm">Create one to start playing!</p>
              </div>
            ) : activeGames.length === 0 ? null : (
              <AnimatePresence>
                {activeGames.map((game) => (
                  <P2PGameCard
                    key={game.game_id}
                    game={game}
                    currentUser={user}
                    onJoin={handleJoinGame}
                    onCancel={handleCancelGame}
                    joining={joining}
                  />
                ))}
              </AnimatePresence>
            )}
          </div>
        </>
      ) : (
        /* House Mode */
        <div className="glass rounded-2xl p-8">
          {/* Show Result for 1 minute after game */}
          <AnimatePresence>
            {gameResult && !flipping && (
              <div className="mb-6">
                <HouseGameResult 
                  result={gameResult} 
                  user={user} 
                  onClose={clearGameResult}
                />
              </div>
            )}
          </AnimatePresence>

          <form onSubmit={handleHousePlay} className="space-y-6">
            <div>
              <Label className="text-slate-300 mb-2 block">Bet Amount (R$)</Label>
              <Input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                min="10"
                className="input-default"
                placeholder="100"
              />
              <p className="text-slate-500 text-sm mt-1">Potential win: R$ {((parseFloat(betAmount) || 0) * 1.98).toFixed(0)} (1.98x)</p>
            </div>
            
            <div>
              <Label className="text-slate-300 mb-2 block">Your Choice</Label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setChoice('bux')}
                  className={`flex-1 p-6 rounded-xl border-2 transition-all flex flex-col items-center gap-3 ${
                    choice === 'bux'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <BuxChip size={64} />
                  <span className="text-white font-medium">R$ (Bux)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setChoice('tix')}
                  className={`flex-1 p-6 rounded-xl border-2 transition-all flex flex-col items-center gap-3 ${
                    choice === 'tix'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <TixChip size={64} />
                  <span className="text-white font-medium">Tix</span>
                </button>
              </div>
            </div>

            {/* Coin Animation */}
            {flipping && (
              <div className="flex justify-center py-8">
                <SpinningCoin showResult={false} resultSide={null} />
              </div>
            )}
            
            <Button type="submit" disabled={loading || flipping} className="w-full btn-primary h-12 text-lg">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : `Flip for R$${betAmount || 0}`}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
